import { Upload01Icon } from "@/icons";
import { cn } from "@/utils/tailwind";
import { openFileBrowser } from "@/utils/upload";
import {
  ComponentType,
  DragEventHandler,
  PropsWithChildren,
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";

type IconType = ComponentType<{ className?: string }>;

enum FileBrowsingMode {
  File = "file",
  Directory = "directory",
}

export interface FileDropZoneProps extends PropsWithChildren {
  value?: File[];
  onChange?: (files: File[]) => void;
  onBlur?: () => void;
  icon?: IconType;
  multiple?: boolean;
  accept?: string;
  directory?: boolean;
}

export interface FileDropZoneHandle {
  focus(): void;
}

export default forwardRef(function FileDropZone(
  {
    value,
    onChange,
    onBlur,
    icon: Icon = Upload01Icon,
    children,
    multiple = false,
    accept = "",
    directory = false,
  }: FileDropZoneProps,
  ref: Ref<FileDropZoneHandle>,
) {
  const [isDragging, setDragging] = useState(false);
  const [fileBrowsingMode, setFileBrowsingMode] = useState<FileBrowsingMode>();

  const onDragOver: DragEventHandler = (evt) => {
    evt.preventDefault();
    setDragging(true);
  };

  const onDragLeave: DragEventHandler = () => {
    setDragging(false);
  };

  const onDrop: DragEventHandler = (evt) => {
    evt.preventDefault();
    setDragging(false);
    onSelectFiles(...evt.dataTransfer.files);
  };

  const onBrowse = (evt?: React.MouseEvent, directory = false) => {
    evt?.preventDefault();
    setFileBrowsingMode(
      directory ? FileBrowsingMode.Directory : FileBrowsingMode.File,
    );
  };

  const onSelectFiles = useCallback(
    (...files: File[]) => {
      if (files.length) {
        onChange?.(value ? value.concat(...files) : files);
      }
      onBlur?.();
    },
    [value, onChange, onBlur],
  );

  useEffect(() => {
    if (fileBrowsingMode) {
      openFileBrowser(
        fileBrowsingMode == FileBrowsingMode.File
          ? { multiple, accept }
          : { multiple: false, webkitdirectory: true },
      ).then((files) => {
        onSelectFiles(...files);
        setFileBrowsingMode(undefined);
      });

      // NOTE: page is suspended until file picker is closed, no need to return a cleanup
    }
  }, [fileBrowsingMode, onSelectFiles, multiple, accept, directory]);

  useImperativeHandle(ref, () => ({
    focus() {
      onBrowse();
    },
  }));

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        "w-full flex flex-col items-center justify-center p-16 border border-dashed rounded-lg gap-3 transition-colors",
        isDragging
          ? "bg-brand-25 border-brand-500"
          : "bg-linear-to-b from-white to-grey-50 border-brand-100",
      )}
    >
      <Icon
        className={cn(
          "w-8 h-8 transition-colors",
          isDragging ? "text-brand-500" : "text-grey-400",
        )}
      />
      <p className="text-center font-medium text-grey-900">
        <button className="text-brand-500" onClick={onBrowse}>
          <FormattedMessage
            defaultMessage="Upload {multiple, plural, one {file} other {files}}"
            values={{ multiple: multiple ? 2 : 1 }}
          />
        </button>{" "}
        {directory && (
          <>
            <span>
              <FormattedMessage defaultMessage="or" />{" "}
            </span>
            <button
              className="text-brand-500"
              onClick={(evt) => onBrowse(evt, true)}
            >
              <FormattedMessage
                defaultMessage="upload directory"
                values={{ multiple: multiple ? 2 : 1 }}
              />
            </button>{" "}
          </>
        )}
        <span>
          <FormattedMessage defaultMessage="or drag and drop" />
        </span>
      </p>
      {children}
    </div>
  );
});
